import { useState } from 'react'
import { Navigate } from 'react-router-dom'

import { Alert, Button, Text } from '@/ui'
import { TypographyStyles } from '@/styles'
import { US_STATE_SELECT_OPTIONS } from '@/constants/locationConstants'
import { reloadLocation } from '@/utils/browserNavigation'

import { COMPLIANCE_PATHS } from '../lib/constants'
import { TierCard } from './TierCard'
import { MissingSignalsDialog } from './MissingSignalsDialog'
import type { PlanCheckout } from './usePlanCheckout'

interface CompliancePlanSectionProps {
  /** Everything this renders from, owned by the page so the summary beside the
   *  cards and the cards themselves cannot disagree about the chosen plan. */
  plan: PlanCheckout
}

/**
 * Whichever plan-choosing UI this practice has earned: the tier cards, or the
 * one thing standing between them and the cards.
 *
 * Verdict routing happens before this renders — the report's closing card withholds the
 * link for a disqualified or waitlisted practice, and `QualifiedOnlyRoute` turns the same
 * cohort back if they reach the route directly.
 *
 * Presentational apart from the two dialogs' own open/closed state: choosing a
 * tier selects it, and the page's Pay Now is what reaches Stripe.
 */
export const CompliancePlanSection: React.FC<CompliancePlanSectionProps> = ({
  plan,
}) => {
  const [collecting, setCollecting] = useState(false)
  const { state, homeState } = plan

  // Recommendations off, or the fetch failed — the report stands on its own, so
  // render no plan block rather than an error.
  if (state.status === 'loading' || state.status === 'unavailable') return null

  if (state.status === 'needsHomeState') {
    return (
      <section className="flex flex-col gap-4">
        <Text variant={TypographyStyles.DISPLAY_XS}>
          One more thing before you pay
        </Text>
        <Text variant={TypographyStyles.BODY_MD}>
          We need the state your practice is based in to set up your taxes.
        </Text>
        <label className="flex flex-col gap-2">
          <Text variant={TypographyStyles.BODY_SM}>
            Which state is your practice based in?
          </Text>
          <select
            aria-label="Which state is your practice based in?"
            className="rounded-lg border p-2"
            value={homeState.value}
            onChange={(event) => homeState.set(event.target.value)}
          >
            <option value="">Select a state</option>
            {US_STATE_SELECT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        {homeState.error && <Alert variant="error">{homeState.error}</Alert>}
        <Button
          onClick={homeState.saveAndRetry}
          disabled={!homeState.value || homeState.saving || plan.busy}
          loading={homeState.saving || plan.busy}
        >
          Save and continue
        </Button>
      </section>
    )
  }

  if (state.status === 'insufficientSignals') {
    return (
      <section className="flex flex-col gap-4">
        <Text variant={TypographyStyles.DISPLAY_XS}>A few more questions</Text>
        <Text variant={TypographyStyles.BODY_MD}>
          We need a little more about your practice before we can recommend a
          plan.
        </Text>
        <Button onClick={() => setCollecting(true)}>Add your details</Button>
        <MissingSignalsDialog
          missing={state.missing}
          open={collecting}
          onClose={() => setCollecting(false)}
          onSaved={() => {
            setCollecting(false)
            plan.refetch()
          }}
        />
      </section>
    )
  }

  // No offers means no eligibility rule matches this practice, so there is nothing to
  // choose here. Returning them to the report keeps them in the readiness funnel — the
  // previous link into the signup funnel stranded them on a step they never started.
  if (state.status === 'noOffers') {
    return <Navigate to={COMPLIANCE_PATHS.report} replace />
  }

  // The plan we are steering toward is read first. Sorted here rather than positioned with
  // CSS `order` on the card: these are radios in a `radiogroup` and every one of them is
  // tabbable, so `order` would leave the eye following one sequence, the tab key a second
  // and a screen reader a third (WCAG 1.3.2, 2.4.3). Sorting moves all three together.
  //
  // Ordering also belongs to the list rather than the leaf — a card knows nothing about its
  // siblings, so a card asserting it goes first silently reorders any future consumer.
  //
  // Stable within each group: `isRecommended` is fixed for the session, so this cannot
  // re-sort under a customer who is tapping between tiers.
  const recommendedFirst = [...state.offers].sort(
    (a, b) => Number(b.isRecommended) - Number(a.isRecommended)
  )

  // `ready` and `unpriced` both show the cards: an unpriceable preview is still
  // a set the customer can choose a different tier from.
  return (
    <section className="flex flex-col gap-6">
      {plan.errorMessage && (
        <Alert
          variant="error"
          ctaText="Refresh page"
          ctaOnClick={reloadLocation}
        >
          {plan.errorMessage}
        </Alert>
      )}
      <div
        role="radiogroup"
        aria-label="Plans"
        className="flex flex-col gap-6 md:flex-row"
      >
        {recommendedFirst.map((offer) => (
          <TierCard
            key={offer.tier}
            offer={offer}
            interval={plan.interval}
            selected={offer.tier === state.previewOffer.tier}
            onSelect={plan.selectOffer}
            disabled={plan.busy}
            showRecommendation={recommendedFirst.length > 1}
            freeTrialEnabled={plan.freeTrialEnabled}
          />
        ))}
      </div>
    </section>
  )
}
